import { Formik } from 'formik';
import React, { useEffect, useRef, useState } from 'react';
import * as yup from 'yup';
import StepFooter from '../StepFooter';
import { useHistory } from 'react-router-dom';
import classNames from 'classnames';
import { useAppSelector } from '@/hooks/useAppSelector';
import { orderV2Selectors } from '@/redux/selectors/order.v2';
import { usePaymentContext } from '@/mp/hooks/usePaymentContext';
import text from '@/text';
import { isStaff } from '@/utilities/user';
import useUser from '@/mp/hooks/useUser';
import { isJobPayableOrQuoted } from '@/utilities/job';
import { isMembershipPriceId } from '@/utilities/membership';
import {
  rtkApi,
  useCreateInvoiceActivityMutation,
  useGetJobQuery,
  usePayOrderStageAMutation,
  usePayOrderStageBMutation,
  usePayOrderStageCMutation,
} from '@/redux/rtk';
import Step from '@/components/wizard/Step';
import ConfirmationForm from './ConfirmationForm';
import { useStripe } from '@stripe/react-stripe-js';
import StripeElements from '@/components/stripe/StripeElements';
import routes from '@/routes/constants';
import { useLeads } from '@/mp/hooks/useLeads';
import { useQuote2dMap } from '@/hooks/useQuote2dMap';
import useRedirectToMap from '../../../hooks/useRedirectToMap';
import { useCommitQuote } from '@/mp/hooks/useCommitQuote';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { getUserProfile } from '@/redux/actions';
import { confirmStripePayment } from '@/utilities/stripe';
import { captureError } from '@/utilities/error';

const validationSchema = yup.object({
  terms: yup.bool().oneOf([true]),
});
export type ConfirmationFormValues = {
  terms: boolean;
};

const ConfirmationStep = () => {
  const currentUser = useUser();
  const dispatch = useAppDispatch();
  const history = useHistory();

  const { jobId, asMember, formatPrice, committedRequestForQuoteId, payQuoteId } =
    usePaymentContext();
  const quote = useAppSelector((state) =>
    orderV2Selectors.getQuote(state, jobId, asMember)
  );
  const totalNet =
    quote?.buyNowTotals?.totalNet ?? quote?.nonRfq.totals.totalNet;
  const membershipPriceId = useAppSelector(
    (state) => state.order.membershipPriceId
  );

  const paymentMethodId = useAppSelector(
    (state) => state.order.paymentMethodId
  );

  const getJob = useGetJobQuery(jobId);
  const reduxCustomerSoftware = useAppSelector(
    orderV2Selectors.getCustomerSoftware
  );
  const reduxComments = useAppSelector(orderV2Selectors.getComments);
  const customerSoftware =
    getJob.data?.customer_software ?? reduxCustomerSoftware;
  const comments = getJob.data?.message ?? reduxComments;

  const [isPayOrderLoading, setIsPayOrderLoading] = useState(false);
  const { getCheckoutState } = useQuote2dMap();
  // const [initiateQuote] = useInitiateQuoteMutation();
  const [payOrderStageA] = usePayOrderStageAMutation();
  const [payOrderStageB] = usePayOrderStageBMutation();
  const [payOrderStageC] = usePayOrderStageCMutation();
  const {
    sendConfirmationPageLead,
    sendConfirmationPageLeadState,
    sendPaidLead,
  } = useLeads();
  const isConfirmationPageLeadLoading = useRef(false);

  const stripe = useStripe();

  const [createInvoiceActivity] = useCreateInvoiceActivityMutation();
  const committedQuoteId = useCommitQuote();
  // Pay Invoice mode pays the existing invoiced quote; the normal flow pays the
  // quote we just committed.
  const payTargetQuoteId = payQuoteId ?? committedQuoteId;

  useEffect(() => {
    // Paying an existing invoice → its activity already exists; don't mint a new one.
    if (payQuoteId) return;
    if (!committedQuoteId) return;
    if (typeof totalNet !== 'number') return;

    createInvoiceActivity({ quoteId: committedQuoteId, amount: totalNet })
      .unwrap()
      .catch((err) => {
        captureError(err);
        console.error('Failed to create invoice activity:', err);
      });
  }, [payQuoteId, committedQuoteId, totalNet, createInvoiceActivity]);

  useEffect(() => {
    isConfirmationPageLeadLoading.current =
      sendConfirmationPageLeadState.isLoading;
  }, [sendConfirmationPageLeadState.isLoading]);

  useEffect(() => {
    if (isConfirmationPageLeadLoading.current) {
      return;
    }
    isConfirmationPageLeadLoading.current = true;
    if (!quote) {
      return;
    }
    if (!customerSoftware) {
      return;
    }
    sendConfirmationPageLead({
      jobId,
      data: { quote, architecturalSoftware: customerSoftware, comments },
    });
    isConfirmationPageLeadLoading.current = false;
  }, [comments, customerSoftware, jobId, quote, sendConfirmationPageLead]);

  const handleSubmit = async () => {
    if (!quote) {
      console.warn('no quote');
      return;
    }
    if (!customerSoftware) {
      throw new Error('Customer software is not set');
    }
    if (!payTargetQuoteId) {
      throw new Error('No quote to pay');
    }

    const checkoutState = await getCheckoutState(jobId);

    try {
      setIsPayOrderLoading(true);
      // create RFQ activity to be visible in the file manager
      const params: string[][] = [];
      const respA = await payOrderStageA({
        quoteId: payTargetQuoteId,
        data: { paymentMethodId },
      }).unwrap();
      const membershipPaymentIntentId = respA.paymentIntentId;
      if (membershipPaymentIntentId) {
        params.push(['membershipPaymentIntentId', membershipPaymentIntentId]);
      }
      if (respA.paymentIntentClientSecret) {
        await confirmStripePayment({
          stripe,
          paymentMethodId,
          paymentIntentClientSecret: respA.paymentIntentClientSecret,
        });
        params.push([
          'membershipClientSecret',
          respA.paymentIntentClientSecret,
        ]);
      }
      const respB = await payOrderStageB({
        quoteId: payTargetQuoteId,
        data: {
          message: comments,
          software: customerSoftware,
          rfqId: committedRequestForQuoteId,
        },
      }).unwrap();
      const orderPaymentIntentId = respB.paymentIntentId;
      if (orderPaymentIntentId) {
        params.push(['orderPaymentIntentId', orderPaymentIntentId]);
      }
      if (respB.paymentIntentClientSecret) {
        await confirmStripePayment({
          stripe,
          paymentMethodId,
          paymentIntentClientSecret: respB.paymentIntentClientSecret,
        });
      }
      await payOrderStageC({
        quoteId: payTargetQuoteId,
        data: {
          checkoutState,
        },
      });

      sendPaidLead({
        jobId,
        data: { quote, architecturalSoftware: customerSoftware, comments },
      })
        .unwrap()
        .catch((err) => {
          captureError(err);
          console.error('Failed to send paid lead:', err);
        });

      if (respB.paymentIntentClientSecret) {
        // refresh legacy redux state e.g. UserNav
        if (respA.paymentIntentId || respA.paymentIntentClientSecret) {
          dispatch(getUserProfile());
          rtkApi.util.invalidateTags(['User']);
        }

        history.push({
          pathname: routes.payment.status(jobId),
          search: new URLSearchParams(params).toString(),
        });
      } else {
        const projectId = getJob.data?.project.id;
        if (projectId) {
          history.push(routes.fileManager.viewProject(projectId));
        }
      }
    } catch (error) {
      captureError(
        new Error('Failed to pay order: ' + (error as Error).message)
      );
      console.error('Failed to pay order:', error);
    } finally {
      setIsPayOrderLoading(false);
    }
  };

  const { redirectToMap } = useRedirectToMap();

  return (
    <Formik
      initialValues={{
        terms: false,
      }}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      validateOnMount
    >
      {({ isValid }) => (
        <>
          {/* @ts-expect-error - Step component is not typed */}
          <Step>
            <ConfirmationForm disabled={isPayOrderLoading} />
          </Step>
          <StepFooter
            submit
            onCancel={redirectToMap}
            previousButtonProps={{
              disabled: isPayOrderLoading,
            }}
            nextButtonProps={{
              type: 'submit',
              loading: isPayOrderLoading,
              label: (
                <span
                  style={{ fontSize: 16 }}
                  className={classNames({
                    'green-1': !isValid,
                  })}
                >
                  {totalNet === 'TBC'
                    ? `${text('toBeConfirmed')} - `
                    : totalNet
                      ? `${formatPrice(totalNet)} - `
                      : ''}
                  {text(
                    isStaff(currentUser.profile?.role)
                      ? 'commitOrder'
                      : isJobPayableOrQuoted(getJob.data) ||
                          isMembershipPriceId(membershipPriceId)
                        ? 'payNow'
                        : 'order'
                  )}
                </span>
              ),
              disabled: !isValid,
            }}
          />
        </>
      )}
    </Formik>
  );
};

const ConfirmationStepWithStripe = () => {
  return (
    <StripeElements>
      <ConfirmationStep />
    </StripeElements>
  );
};

export default ConfirmationStepWithStripe;
